import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TextField,
  Autocomplete,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from 'xlsx';
import useApi from 'hooks/useApi';
import useSelection from 'hooks/useSelection';
import DownloadTemplateSection from 'components/DownloadTemplateSection';
import { InputComponents } from 'types/auth';
import { createDateWithTimezone, getCurrentTimezone } from 'utils/dateFormat';
import {
  IGNORE_VALUE,
  NON_IMPORTABLE_TYPES,
  VALUE_MAPPABLE_TYPES,
  fromWireMapping,
  importableFieldsOf,
  isTruthy,
  makeDisplayLabel,
  normalizeHeader,
  reachableStepsOf,
  splitMulti,
  stepLabel,
  toWireMapping
} from 'utils/importMapping';

type RowResult = {
  row: number;
  status: 'success' | 'partial' | 'error';
  message: string;
};

interface ImportTransactionsDialogProps {
  open: boolean;
  onClose: () => void;
  teamOptions: any[];
  selectedTeam: string;
  onImported?: () => void;
}

const resourceLabel = (r: any): string => {
  if (!r) return '';
  if (r.fields && typeof r.fields === 'object') {
    const vals = Object.values(r.fields).filter((v: any) => (typeof v === 'string' || typeof v === 'number') && v !== '');
    if (vals.length) return String(vals[0]);
  }
  return r.name || r.id || 'Unnamed resource';
};

// A ResourceType's fields are completely free-form — whatever the org
// defined (a "Driver" type might split a name into firstName/lastName; a
// "Vehicle" type might have nothing name-like at all, just plate/model/
// capacity). There's no schema-level "this is the name field" — nothing
// like it exists on the ResourceType model, and ResourceTableView renders
// every field as its own column with no special one singled out.
// resourceLabel above is only a DISPLAY guess (the first non-empty
// field), so it must not be used to validate a match: if a word landed in
// some OTHER field than whichever one resourceLabel happened to pick, a
// real match would get wrongly rejected. This checks every one of the
// resource's own field values instead, regardless of which field each
// word actually lives in.
const resourceContainsAllWords = (r: any, lowerCaseWords: string[]): boolean => {
  if (!r?.fields || typeof r.fields !== 'object') return false;
  const haystack = Object.values(r.fields)
    .filter((v: any) => typeof v === 'string' || typeof v === 'number')
    .map((v: any) => String(v).toLowerCase());
  return lowerCaseWords.every((w) => haystack.some((v) => v.includes(w)));
};

// The manual Add Transaction form (see formData.tsx's ResourceSelector) treats
// field.resourceList — an array of resource type ids — as "the authoritative
// source for what's searchable" on a RESOURCE field, falling back to the
// single field.resourceType only for legacy data with no resourceList set.
// A field can easily be configured to search across MORE than one resource
// type (e.g. "Crew Information" searching both Crew and Driver records) — so
// searching by field.resourceType alone, as this importer originally did,
// silently misses matches that exist under one of the field's OTHER
// searchable types. This mirrors that same effective-types logic.
// A resource type id can show up as a bare string or, on some field shapes,
// as an object ({id}/{uid}/{_id}) — normalize either to the string the
// backend expects.
const asTypeId = (v: any): string | null => {
  if (!v) return null;
  if (typeof v === 'string') return v;
  return v.id || v.uid || v._id || null;
};

const resourceTypesForField = (field: any): string[] => {
  const rawList =
    field?.resourceList || field?.settings?.resourceList || field?.component?.resourceList || field?.component?.settings?.resourceList;
  if (Array.isArray(rawList) && rawList.length > 0) {
    const ids = rawList.map(asTypeId).filter(Boolean) as string[];
    if (ids.length > 0) return ids;
  }
  const single = asTypeId(
    field?.resourceType ||
      field?.settings?.resourceType ||
      field?.resourceTypeId ||
      field?.resourceTypeUid ||
      field?.settings?.resourceTypeId ||
      field?.component?.resourceType ||
      field?.component?.settings?.resourceType
  );
  if (single) return [single];
  // Nothing resolvable — the backend flatly rejects a search with no type
  // (`resource/search` 404s "Resource type not found" when neither `type`
  // nor `types` is sent, see resource-directory.usecase.provider.ts), so a
  // field that reaches this point can never auto-match OR manually search
  // until its workflow config actually carries a resource type. Logged
  // loudly (not just swallowed) so this is diagnosable from the browser
  // console instead of looking identical to "the record doesn't exist".
  // eslint-disable-next-line no-console
  console.warn('[ImportTransactionsDialog] RESOURCE field has no resolvable resourceType/resourceList — search will fail:', field);
  return [];
};

/** A single "raw value found in the file" -> "resource record" row, with its own live search. */
const ResourceValueRow: React.FC<{
  rawValue: string;
  resourceTypes: string[];
  value: any;
  onChange: (v: any) => void;
  searchResources: (resourceType: string | null, query: string, resourceTypes?: string[]) => Promise<any>;
  getResourceTypeById: (resourceTypeId: string) => Promise<any>;
  createResourceData: (resourceTypeId: string, data: { [key: string]: any }, additionalPayload?: { [key: string]: any }) => Promise<any>;
  showToast: (message: string, severity?: any) => void;
}> = ({ rawValue, resourceTypes, value, onChange, searchResources, getResourceTypeById, createResourceData, showToast }) => {
  const [inputValue, setInputValue] = useState(rawValue);
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Inline "the record doesn't exist yet" creation, so a name in the
  // spreadsheet that has no resource behind it can be dealt with here instead
  // of abandoning the import to go and create it in Resources first.
  const [addOpen, setAddOpen] = useState(false);
  const [addTypeId, setAddTypeId] = useState<string>('');
  const [addTypes, setAddTypes] = useState<any[]>([]);
  const [addFields, setAddFields] = useState<any[]>([]);
  const [addValues, setAddValues] = useState<Record<string, any>>({});
  const [addLoading, setAddLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // A primitive key for the effect's dep array — resourceTypes is a fresh
  // array reference on every parent render, which would otherwise re-fire
  // this effect (and re-search) constantly even with no real change.
  const resourceTypesKey = resourceTypes.join(',');

  // `value` and `inputValue` are BOTH controlled here, and MUI's Autocomplete
  // only ever shows `inputValue` as the box's visible text — it does not
  // derive that text from `value` on its own. The bulk auto-match effect up
  // in the parent resolves `value` asynchronously (a network search), and
  // without this the box would keep showing the raw file text / placeholder
  // forever even after a real match landed, which is exactly what made this
  // look identical to "nothing is happening" even when matching succeeded.
  // This keeps the visible text following whatever `value` actually is —
  // matched (auto or manual) shows the match's label, unmatched falls back
  // to the raw file value so there's still something to search from.
  useEffect(() => {
    setInputValue(value ? resourceLabel(value) : rawValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    let active = true;
    if (!inputValue) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      // Wrapped in an async IIFE with try/catch/finally so ANY failure — a
      // thrown error, a bad response shape, a hung request — still clears the
      // loading state instead of leaving the dropdown stuck on "Loading…".
      // A race against a hard timeout guards against a request that never
      // settles at all.
      (async () => {
        try {
          const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Search timed out')), 8000));
          const resp: any = await Promise.race([searchResources(null, inputValue, resourceTypes), timeout]);
          if (!active) return;
          const list = Array.isArray(resp)
            ? resp
            : Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp?.data?.data)
            ? resp.data.data
            : [];
          setOptions(list);
        } catch {
          if (active) setOptions([]);
        } finally {
          if (active) setLoading(false);
        }
      })();
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, resourceTypesKey]);

  /** Load the field definitions for every type this field can search, so the
   * create form matches whichever type the user picks. */
  const openAddDialog = async () => {
    if (resourceTypes.length === 0) return;
    setAddOpen(true);
    setAddLoading(true);
    try {
      const loaded = await Promise.all(
        resourceTypes.map(async (id) => {
          try {
            const resp = await getResourceTypeById(id);
            return resp?.success && resp.data ? resp.data : null;
          } catch {
            return null;
          }
        })
      );
      const types = loaded.filter(Boolean);
      setAddTypes(types);
      const first = types[0];
      if (first) selectAddType(first);
    } finally {
      setAddLoading(false);
    }
  };

  const selectAddType = (type: any) => {
    setAddTypeId(type.id);
    // Team dropdowns are excluded the same way the manual Add Transaction
    // form excludes them — they're not something to type in by hand here.
    const fields = (type.fields || []).filter((f: any) => f.dropdownSource !== 'teams');
    setAddFields(fields);
    const initial: Record<string, any> = {};
    fields.forEach((f: any) => {
      initial[f.name] = '';
    });
    // Pre-fill the first text field with the value from the file — that's the
    // name the user is trying to create, so making them retype it is busywork.
    const firstText = fields.find((f: any) => f.type === 'text' || f.type === 'textarea');
    if (firstText) initial[firstText.name] = rawValue;
    setAddValues(initial);
  };

  const handleSaveResource = async () => {
    const type = addTypes.find((t: any) => t.id === addTypeId);
    if (!type) return;
    setSaving(true);
    try {
      // A `user` resource type also wants the field values flattened onto the
      // payload root, the way ResourceDataManager sends them.
      const additionalPayload = type.category === 'user' ? { category: 'user', ...addValues } : undefined;
      const resp = await createResourceData(type.id, addValues, additionalPayload);
      if (!resp?.success) {
        showToast(resp?.message || 'Could not create that resource.', 'error');
        return;
      }
      const created = resp.data || {};
      // The created record isn't fully trusted to come back shaped like a
      // search hit, and both halves are already in hand here — so fall back to
      // the values just submitted and the type's own category.
      onChange({
        id: created.id || created._id,
        fields: created.fields || addValues,
        category: created.category || created.resourceType?.category || created.resourceTypeId?.category || type.category
      });
      showToast('Resource created', 'success');
      setAddOpen(false);
    } catch (err: any) {
      showToast(err?.message || 'Could not create that resource.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const missingRequired = addFields.some((f: any) => f.required && !String(addValues[f.name] ?? '').trim());

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Autocomplete
          size="small"
          options={options}
          loading={loading}
          value={value || null}
          getOptionLabel={resourceLabel}
          isOptionEqualToValue={(a: any, b: any) => a?.id === b?.id}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          onChange={(_, v: any) =>
            onChange(
              v ? { id: v.id, fields: v.fields, category: v.category || v.resourceType?.category || v.resourceTypeId?.category } : null
            )
          }
          noOptionsText={
            loading ? (
              'Searching…'
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No matching resources
                </Typography>
                {resourceTypes.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    // The dropdown's no-options box closes on blur, so this
                    // has to fire on mousedown to survive the click.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      openAddDialog();
                    }}
                  >
                    Add
                  </Button>
                )}
              </Box>
            )
          }
          renderInput={(params) => <TextField {...params} placeholder="Type here to search resource…" />}
          sx={{ minWidth: 240, flex: 1 }}
        />
        {/* Also reachable without emptying the search box first — an unmatched
            row shows results for a near-miss just as often as none at all. */}
        {!value && resourceTypes.length > 0 && (
          <Tooltip title="Create this resource">
            <IconButton size="small" onClick={openAddDialog}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Resource</DialogTitle>
        <DialogContent dividers>
          {addLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading the resource form…</Typography>
            </Box>
          ) : addTypes.length === 0 ? (
            <Alert severity="error">Could not load the resource type for this field, so a new record can&apos;t be created here.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Only asked when the field genuinely searches more than one
                  type — otherwise there's nothing to choose. */}
              {addTypes.length > 1 && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Resource type"
                  value={addTypeId}
                  onChange={(e) => {
                    const t = addTypes.find((x: any) => x.id === e.target.value);
                    if (t) selectAddType(t);
                  }}
                >
                  {addTypes.map((t: any) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {addFields.map((field: any) => {
                // Images and license verification need in-app capture, so
                // they can't be filled here — flagged rather than rendered as
                // a text box that would silently store a useless string.
                if (field.type === 'image' || field.type === 'license_verification') {
                  return (
                    <Alert severity="info" key={field.name}>
                      <strong>{field.name}</strong> is {field.type === 'image' ? 'an image' : 'a license verification'} field and can&apos;t
                      be captured here. The record will be created without it.
                    </Alert>
                  );
                }
                const isDropdown = field.type === 'dropdown' && (field.dropdownOptions || []).length > 0;
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    size="small"
                    select={isDropdown}
                    label={field.name}
                    required={field.required}
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 3 : undefined}
                    value={addValues[field.name] ?? ''}
                    onChange={(e) => setAddValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                  >
                    {isDropdown && <MenuItem value="">Please select</MenuItem>}
                    {isDropdown &&
                      (field.dropdownOptions || []).map((opt: string) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                  </TextField>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveResource}
            disabled={saving || addLoading || addTypes.length === 0 || missingRequired}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/** One hand-typed cell for a required field no column fills — the right input
 * for the field's type, rendered once per row of the uploaded file. */
const ManualValueInput: React.FC<{
  field: any;
  value: any;
  onChange: (v: any) => void;
  searchResources: (resourceType: string | null, query: string, resourceTypes?: string[]) => Promise<any>;
  getResourceTypeById: (resourceTypeId: string) => Promise<any>;
  createResourceData: (resourceTypeId: string, data: { [key: string]: any }, additionalPayload?: { [key: string]: any }) => Promise<any>;
  showToast: (message: string, severity?: any) => void;
}> = ({ field, value, onChange, searchResources, getResourceTypeById, createResourceData, showToast }) => {
  switch (field.inputComponent) {
    case InputComponents.RESOURCE:
      return (
        <ResourceValueRow
          rawValue=""
          resourceTypes={resourceTypesForField(field)}
          value={value}
          onChange={onChange}
          searchResources={searchResources}
          getResourceTypeById={getResourceTypeById}
          createResourceData={createResourceData}
          showToast={showToast}
        />
      );
    case InputComponents.SWITCH:
      return (
        <FormControl size="small" fullWidth>
          <Select displayEmpty value={value === undefined ? '' : String(!!value)} onChange={(e) => onChange(e.target.value === 'true')}>
            <MenuItem value="">
              <em>Choose…</em>
            </MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
      );
    case InputComponents.CHECKBOX:
      return (
        <FormControl size="small" fullWidth>
          <Select
            multiple
            displayEmpty
            value={Array.isArray(value) ? value : []}
            onChange={(e) => onChange(e.target.value as string[])}
            renderValue={(selected: any) => (selected.length ? selected.join(', ') : 'Choose…')}
          >
            {(field.options || []).map((opt: string) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case InputComponents.SELECT:
    case InputComponents.RADIO:
      return (
        <FormControl size="small" fullWidth>
          <Select displayEmpty value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            <MenuItem value="">
              <em>Choose…</em>
            </MenuItem>
            {(field.options || []).map((opt: string) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    default:
      return (
        <TextField
          size="small"
          fullWidth
          type={
            field.inputComponent === InputComponents.NUMBER
              ? 'number'
              : field.inputComponent === InputComponents.DATE
              ? field.settings?.isTimeRequired
                ? 'datetime-local'
                : 'date'
              : 'text'
          }
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

const ImportTransactionsDialog: React.FC<ImportTransactionsDialogProps> = ({ open, onClose, teamOptions, selectedTeam, onImported }) => {
  const {
    getFlowByTeamUid,
    getImportMapping,
    saveImportMapping,
    uploadEventData,
    searchResources,
    getResourceTypeById,
    createResourceData,
    showToast
  } = useApi();
  const { browserDetails, fingerprint, publicIp, captureLocationWithAddress, location } = useSelection();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Step 0 — upload
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Every step defined in this process's workflow, in flow order (index 0 =
  // the step that creates the transaction) — the whole process, not just the
  // steps the importing user's own role would normally be able to act on.
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [steps, setSteps] = useState<any[]>([]);

  // Step 1 — field mapping: fieldUid -> column name ('' = not imported)
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});

  // The mapping saved for this process by a previous import, fetched on open
  // and applied as soon as a file is parsed so both mapping screens come back
  // pre-filled. `missingSavedColumns` are saved columns the uploaded file
  // doesn't have — those entries get dropped, and the user is told which.
  const [savedMapping, setSavedMapping] = useState<any | null>(null);
  const [appliedSavedMapping, setAppliedSavedMapping] = useState(false);
  const [missingSavedColumns, setMissingSavedColumns] = useState<string[]>([]);

  // Step 2 — value mapping: fieldUid -> { rawValue -> mapped value }
  const [valueMappings, setValueMappings] = useState<Record<string, Record<string, any>>>({});
  const [resourceMatching, setResourceMatching] = useState<Record<string, boolean>>({});

  // Step 2 also — values typed in by hand for a required field that no column
  // in the file fills. Held PER ROW, not once per field: these are usually
  // genuinely different for each transaction (a reference number, a per-row
  // date), so a single value for the whole file wouldn't do.
  // fieldUid -> { rowIndex -> value }, rowIndex being the 0-based index into
  // parsedRows (so row 1 in the UI is index 0).
  const [manualValues, setManualValues] = useState<Record<string, Record<number, any>>>({});

  // Inline corrections for a mapped NUMBER/DATE cell whose file value is the
  // wrong type — the user fixes it right in the Review Values table instead of
  // editing the spreadsheet and re-uploading. fieldUid -> { rowIndex -> value },
  // same 0-based rowIndex as manualValues. When set, this value overrides the
  // raw cell everywhere (validation AND the import payload).
  const [cellOverrides, setCellOverrides] = useState<Record<string, Record<number, any>>>({});

  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Step 3 — import
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<RowResult[]>([]);
  const [expectedCount, setExpectedCount] = useState(0);

  const processName = teamOptions.find((t: any) => t.uid === selectedTeam)?.name || 'Process';

  const resetState = () => {
    setFileName(null);
    setParsedRows([]);
    setColumns([]);
    setParseError(null);
    setFieldMapping({});
    setSavedMapping(null);
    setAppliedSavedMapping(false);
    setMissingSavedColumns([]);
    setValueMappings({});
    setManualValues({});
    setCellOverrides({});
    setResourceMatching({});
    setActiveStepIndex(0);
    setResults([]);
    setImporting(false);
    setExpectedCount(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (!selectedTeam || selectedTeam === 'all') return;

    let cancelled = false;
    setLoadingFields(true);
    setLoadError(null);
    setSteps([]);
    setSavedMapping(null);

    // The saved mapping is fetched alongside the workflow so it's already in
    // hand by the time a file is picked. A failure to load it is deliberately
    // NOT fatal — it only costs the user the convenience of a pre-filled
    // mapping, so the import still proceeds from scratch.
    Promise.all([
      getFlowByTeamUid(selectedTeam),
      getImportMapping(selectedTeam).catch((err: any) => {
        // eslint-disable-next-line no-console
        console.warn('[ImportTransactionsDialog] could not load the saved mapping for this process:', err);
        return null;
      })
    ])
      .then(([resp, mappingResp]: any[]) => {
        if (cancelled) return;
        // The full, unfiltered workflow definition for this process — every
        // step in execution order, regardless of the IMPORTING user's own
        // role. (This is the same data source the workflow builder screen
        // uses, as opposed to the role-filtered "steps I can act on" list
        // used by the manual Add Transaction flow.)
        const list = Array.isArray(resp?.data?.steps) ? resp.data.steps : [];
        if (resp?.success && list.length > 0) {
          setSteps(list);
        } else {
          setLoadError(
            'This process has no workflow configured yet, so there is nothing to import into. Please contact your administrator.'
          );
        }
        if (mappingResp?.success && mappingResp?.data) setSavedMapping(fromWireMapping(mappingResp.data));
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load the fields for this process. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoadingFields(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTeam]);

  // A step can be driven automatically from a file as long as it has at
  // least one field — a photo, signature, file upload, or sub-form field on
  // that step doesn't block automation, even if marked required: it simply
  // can't be captured from a spreadsheet, so it's left blank and the rest of
  // the step still gets submitted (see isValueEmpty/getMappedValue below).
  // A step with NO fields at all — a pure "instructions" step with nothing
  // to fill in — is a different case: there's nothing a file could ever
  // supply for it, so rather than stopping the automation chain there, it's
  // simply skipped and import continues straight on to the next form step,
  // anywhere in the process.
  const reachableSteps = useMemo(() => reachableStepsOf(steps), [steps]);
  const isBlocked = !loadError && steps.length > 0 && reachableSteps.length === 0;

  // Flat list of every field across every reachable step, each tagged with
  // the step it belongs to (for grouping the mapping UI and for the payload).
  const importableFields = useMemo(() => importableFieldsOf(reachableSteps), [reachableSteps]);

  // Disambiguate identically-named fields that live on different steps (e.g.
  // two steps both have a "Notes" field) so the mapping UI is unambiguous.
  // Shared with the template dialog so a downloaded template's headers and
  // this screen's auto-map-by-name can't drift apart.
  const displayLabel = useMemo(() => makeDisplayLabel(importableFields), [importableFields]);

  // A step is part of this import iff the user mapped a column to at least
  // one of its fields. This replaces the old explicit "Steps to include"
  // picker: the mapping already says which steps the file covers, so asking
  // separately was asking the same question twice — and it blocked the whole
  // wizard until answered.
  const includedStepUids = useMemo(
    () => new Set<string>(importableFields.filter((f) => fieldMapping[f.uid]).map((f) => f.__step.uid)),
    [importableFields, fieldMapping]
  );

  // ---------- Document parsing ----------

  /**
   * Pre-fill both mapping screens the moment a file is parsed, in three
   * passes, in this order:
   *
   *   1. the mapping saved for this process wins, for every saved column the
   *      uploaded file actually has (anything else is reported, not silently
   *      dropped);
   *   2. auto-map-by-name fills whatever pass 1 left unmapped;
   *   3. saved value mappings are seeded, but only for raw values that
   *      actually occur in this file.
   *
   * Saved mappings win over header-name matching because the saved one is a
   * decision a human already made and confirmed by importing, whereas a
   * header match is a guess.
   */
  const applyMappingDefaults = (cols: string[], rows: Record<string, any>[]) => {
    const colSet = new Set(cols);
    const savedFieldMapping: Record<string, string> = savedMapping?.fieldMapping || {};

    const applied: Record<string, string> = {};
    const missing = new Set<string>();
    const claimed = new Set<string>();
    Object.entries(savedFieldMapping).forEach(([fieldUid, column]) => {
      if (!colSet.has(column)) {
        missing.add(column);
        return;
      }
      // One column fills one field, so a saved mapping that points two fields
      // at the same column keeps only the first.
      if (claimed.has(column)) return;
      applied[fieldUid] = column;
      claimed.add(column);
    });

    setAppliedSavedMapping(Object.keys(applied).length > 0);
    setMissingSavedColumns(Array.from(missing));

    setFieldMapping(() => {
      const next = { ...applied };
      // Pass 2 — header-name auto-suggest for whatever pass 1 left over, on
      // both sides: an unclaimed column and a still-unmapped field.
      cols.forEach((column) => {
        if (claimed.has(column)) return;
        const target = normalizeHeader(column);
        const field = importableFields.find((f) => !next[f.uid] && normalizeHeader(displayLabel(f)) === target);
        if (field) {
          next[field.uid] = column;
          claimed.add(column);
        }
      });
      return next;
    });

    // Only the column -> field mapping is remembered between imports. Value
    // mappings and hand-typed per-row values are both tied to the CONTENTS of
    // one specific file rather than its shape, so they start fresh every time
    // — the Value Mapping screen re-derives its defaults from whatever values
    // this file actually contains.
    setValueMappings({});
    setManualValues({});
    setCellOverrides({});
  };

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        // cellDates: true makes any cell Excel itself formats as a date come
        // back as a real JS Date, instead of the raw day-count number Excel
        // stores underneath (e.g. 46268) — which, left as a number, was
        // getting turned into a literal year ("01 Jan 46268") once it hit
        // `new Date(...)` downstream. See getMappedValue's DATE case below
        // for the remaining fallback (a serial number that slips through
        // some other way, e.g. from a CSV).
        const wb = XLSX.read(data, { type: 'binary', cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as Record<string, any>[];
        if (rows.length === 0) {
          setParseError('That file has no data rows below the header.');
          setParsedRows([]);
          setColumns([]);
        } else {
          setParsedRows(rows);
          const colSet = new Set<string>();
          rows.forEach((row) => {
            Object.keys(row).forEach((k) => colSet.add(k));
          });
          const cols = Array.from(colSet);
          setColumns(cols);
          applyMappingDefaults(cols, rows);
        }
      } catch (err) {
        setParseError('Could not read that file. Please upload a valid Excel (.xlsx, .xls) or CSV file.');
        setParsedRows([]);
        setColumns([]);
      }
    };
    reader.onerror = () => setParseError('Could not read that file.');
    reader.readAsBinaryString(file);
  };

  // ---------- Field mapping ----------

  // `fieldMapping` stays keyed fieldUid -> column, because that's the
  // direction everything downstream reads it in (getMappedValue,
  // distinctValuesForField, includedStepUids, toWireMapping). The mapping
  // TABLE is column-major though — one row per column in the user's file —
  // so this is the reverse view it renders from.
  const columnToFieldUid = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(fieldMapping).forEach(([fieldUid, column]) => {
      // First writer wins, so a mapping that somehow points two fields at one
      // column resolves deterministically instead of flickering.
      if (column && !map[column]) map[column] = fieldUid;
    });
    return map;
  }, [fieldMapping]);

  /** Point one column at one workflow field (or at nothing). The pairing is
   * one-to-one in both directions: assigning a field that another column
   * already fills moves it rather than duplicating it. */
  const setColumnTarget = (column: string, fieldUid: string) => {
    setFieldMapping((prev) => {
      const next = { ...prev };
      // Release whatever this column used to fill...
      Object.keys(next).forEach((uid) => {
        if (next[uid] === column) delete next[uid];
      });
      // ...then claim the new field. Writing next[fieldUid] also detaches
      // that field from any other column it was previously mapped to.
      if (fieldUid) next[fieldUid] = column;
      return next;
    });
  };

  const handleAutoMapByName = () => {
    setFieldMapping((prev) => {
      const next = { ...prev };
      const claimed = new Set(Object.values(next).filter(Boolean));
      columns.forEach((column) => {
        if (claimed.has(column)) return;
        const target = normalizeHeader(column);
        const field = importableFields.find((f) => !next[f.uid] && normalizeHeader(displayLabel(f)) === target);
        if (field) {
          next[field.uid] = column;
          claimed.add(column);
        }
      });
      return next;
    });
  };

  const handleClearMapping = () => setFieldMapping({});

  // Workflow fields grouped by their step, for the destination dropdown.
  const fieldOptionsByStep = useMemo(
    () =>
      reachableSteps
        .map((step) => ({ step, fields: importableFields.filter((f) => f.__step === step) }))
        .filter((g) => g.fields.length > 0),
    [reachableSteps, importableFields]
  );

  // Required fields on a step this import covers that no column fills. These
  // do NOT block Field Mapping — a required field simply missing from the
  // spreadsheet is a normal situation, not a mistake to correct here. They're
  // carried to the Value Mapping screen instead, where the user types a value
  // for each row of the file (see `manualValues`).
  //
  // Only fields on an INCLUDED step are considered: a step with nothing mapped
  // isn't part of this import at all, so nagging about its required fields
  // would make a 10-step process impossible to import one step of.
  //
  // RESOURCE fields ARE included here, unlike in the mapping checks — the
  // reason they're normally exempt is that matching one from a cell is
  // best-effort guesswork, but picking a single record by hand for the whole
  // file is exact, so there's nothing unreliable to excuse.
  const requiredFieldsNeedingValue = importableFields.filter(
    (f) => includedStepUids.has(f.__step.uid) && f.settings?.required && !fieldMapping[f.uid]
  );

  // ---------- Value mapping ----------

  const distinctValuesForField = (field: any): string[] => {
    const col = fieldMapping[field.uid];
    if (!col) return [];
    const set = new Set<string>();
    parsedRows.forEach((row) => {
      const raw = row[col];
      if (field.inputComponent === InputComponents.CHECKBOX) {
        splitMulti(raw).forEach((t) => set.add(t));
      } else {
        const s = String(raw ?? '').trim();
        if (s) set.add(s);
      }
    });
    return Array.from(set);
  };

  // The "Row in file" column must show where a value actually lives in the
  // uploaded file, not the value's position in the deduped list above — a
  // value from the 2nd data row would otherwise show as row 1 just because
  // it happened to be the first distinct value found. Same 1-based
  // numbering as the Import results list ("Row N") — parsedRows already
  // excludes the header, so index 0 is row 1. A value can come from more
  // than one row (e.g. a repeated "Yes"), so this returns every match.
  const rowNumbersForValue = (field: any, targetRaw: string): number[] => {
    const col = fieldMapping[field.uid];
    if (!col) return [];
    const rows: number[] = [];
    parsedRows.forEach((row, idx) => {
      const raw = row[col];
      const matches =
        field.inputComponent === InputComponents.CHECKBOX ? splitMulti(raw).includes(targetRaw) : String(raw ?? '').trim() === targetRaw;
      if (matches) rows.push(idx + 1);
    });
    return rows;
  };

  const fieldsNeedingValueMapping = importableFields.filter(
    (f) => VALUE_MAPPABLE_TYPES.includes(f.inputComponent) && fieldMapping[f.uid] && distinctValuesForField(f).length > 0
  );

  // ---------- Format validation (NUMBER / DATE) ----------

  /** Turn a raw cell (or real Date) into the timezone date object, using the
   * EXACT rules the import payload uses — see the DATE branch of getMappedValue,
   * which delegates here. Keeping this single source of truth is what lets the
   * Review Values validation and the actual submission agree on what counts as
   * a valid date; if they drifted, we could flag a date the importer would have
   * accepted, or vice versa. Returns null for anything we won't accept. */
  const resolveDateValue = (field: any, raw: any) => {
    if (raw === undefined || raw === null || raw === '') return null;
    let dateInput: Date | string;
    if (raw instanceof Date) {
      // A genuine date cell — Excel formatted it as a date and SheetJS read it
      // with cellDates: true, so it arrives already parsed. Use it as-is.
      dateInput = raw;
    } else {
      // A BARE NUMBER is NOT a date. A real date never reaches here as a plain
      // number (it comes through as a Date above); a number like 12345 typed
      // into a date column is a mistake, so reject it instead of reading it as
      // an Excel day-serial (which would silently turn 12345 into a 1933 date).
      // Covers numeric cells (typeof number) and all-digit strings alike.
      if (typeof raw === 'number' || /^\s*[+-]?\d+(\.\d+)?\s*$/.test(String(raw))) return null;
      let s = String(raw).trim();
      // "YYYY-MM-DD HH:MM[:SS]" — a space instead of the ISO "T" separator — is
      // a common way to type or paste a date-with-time by hand. Normalize it to
      // proper ISO rather than relying on every browser's parser accepting it.
      if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/.test(s)) {
        s = s.replace(' ', 'T');
      }
      dateInput = s;
    }
    const resolved = createDateWithTimezone(
      dateInput,
      getCurrentTimezone(),
      !!field.settings?.isTimeRequired,
      !!field.settings?.includeTimezone
    );
    // An unparseable string yields { date: "NaN-NaN-NaN", timestamp: NaN }.
    // Return null for that instead, so a value left uncorrected imports as
    // empty (the same as a blank cell) rather than storing invalid-date garbage.
    if (!resolved || Number.isNaN(resolved.timestamp)) return null;
    return resolved;
  };

  /** The one place that decides whether a mapped NUMBER/DATE cell is the wrong
   * shape. Returns a short human reason, or null when the value is fine. Empty
   * is fine here — emptiness is the required-field check's concern, not this
   * one's — so we only ever flag a value the user actually typed. */
  const cellFormatError = (field: any, raw: any): string | null => {
    if (raw === undefined || raw === null || String(raw).trim() === '') return null;
    if (field.inputComponent === InputComponents.NUMBER) {
      return Number.isNaN(Number(raw)) ? 'not a number' : null;
    }
    if (field.inputComponent === InputComponents.DATE) {
      const resolved = resolveDateValue(field, raw);
      // createDateWithTimezone happily builds { date: "NaN-NaN-NaN", timestamp:
      // NaN } from an unparseable string — a NaN timestamp is the tell-tale.
      return !resolved || Number.isNaN(resolved.timestamp) ? 'not a valid date' : null;
    }
    return null;
  };

  /** The format this field will actually accept, written for the person fixing
   * the file. DATE mirrors resolveDateValue's canonical shapes and whether the
   * field is configured to need a time component. */
  const expectedFormatHint = (field: any): string => {
    if (field.inputComponent === InputComponents.NUMBER) {
      return 'A number, e.g. 42 or 3.14';
    }
    if (field.inputComponent === InputComponents.DATE) {
      return field.settings?.isTimeRequired
        ? 'Date & time: YYYY-MM-DD HH:MM:SS, e.g. 2026-10-07 14:30:00'
        : 'Date: YYYY-MM-DD, e.g. 2026-10-07';
    }
    return '';
  };

  // Mapped NUMBER/DATE fields on an included step are the only ones whose file
  // values can be the wrong TYPE (value-mappable fields translate through the
  // tables below instead, and types without a column have nothing to check).
  const fieldsNeedingFormatCheck = importableFields.filter(
    (f) =>
      (f.inputComponent === InputComponents.NUMBER || f.inputComponent === InputComponents.DATE) &&
      fieldMapping[f.uid] &&
      includedStepUids.has(f.__step.uid)
  );

  type FormatRow = { rowIndex: number; row: number; value: string; reason: string | null; valid: boolean };

  /** The cells of a field that need the user's attention in the Review Values
   * table: either currently the wrong type, OR already corrected inline. A
   * corrected cell is kept (as a resolved/green row) rather than vanishing, so
   * the input the user is typing in doesn't unmount under them and the step
   * stays put while they work. `reason` is null once the (corrected) value
   * parses; `valid` says the same thing for the row marker.
   * - rowIndex: 0-based index into parsedRows, binds the inline fix input.
   * - row: 1-based file row for display. value: the original file value. */
  const formatRowsForField = (field: any): FormatRow[] => {
    const col = fieldMapping[field.uid];
    if (!col) return [];
    const out: FormatRow[] = [];
    parsedRows.forEach((row, idx) => {
      const override = cellOverrides[field.uid]?.[idx];
      const hasOverride = override !== undefined;
      const effective = hasOverride ? override : row[col];
      const reason = cellFormatError(field, effective);
      if (reason || hasOverride) {
        out.push({ rowIndex: idx, row: idx + 1, value: String(row[col] ?? ''), reason, valid: !reason });
      }
    });
    return out;
  };

  const fieldsWithFormatRows = fieldsNeedingFormatCheck
    .map((field) => ({ field, rows: formatRowsForField(field) }))
    .filter((entry) => entry.rows.length > 0);

  // Whether to show (and keep showing) the format-fix section at all: there's
  // at least one wrong-type cell, or one the user has started correcting.
  const showFormatSection = fieldsWithFormatRows.length > 0;

  // Any cell still the wrong type after the user's inline corrections. This
  // holds back Next / Import on the Review Values step until every flagged
  // value has been fixed (a corrected row counts as resolved).
  const hasUnresolvedFormatErrors = fieldsWithFormatRows.some((entry) => entry.rows.some((r) => !r.valid));

  // ---------- Stepper plumbing ----------

  // The values step earns its place if there are file values to translate OR
  // required fields with no column, which are supplied by hand on that screen.
  // It's labelled "Review Values" rather than "Value Mapping" — "mapping" is
  // our word for it, not something a user reading the stepper would follow.
  const needsValueStep = fieldsNeedingValueMapping.length > 0 || requiredFieldsNeedingValue.length > 0 || showFormatSection;
  const wizardSteps = useMemo(
    () => (needsValueStep ? ['Upload', 'Field Mapping', 'Review Values', 'Import'] : ['Upload', 'Field Mapping', 'Import']),
    [needsValueStep]
  );
  const currentStepLabel = wizardSteps[Math.min(activeStepIndex, wizardSteps.length - 1)];

  useEffect(() => {
    if (activeStepIndex > wizardSteps.length - 1) setActiveStepIndex(wizardSteps.length - 1);
  }, [wizardSteps, activeStepIndex]);

  // A primitive signature of "which fields need which values mapped". The two
  // effects below used to fire only on entering a Value Mapping step; now that
  // the field mapping lives on the SAME screen, they have to re-run whenever
  // the user changes a column mapping — otherwise a value table that appears
  // mid-screen would sit there unseeded and (for RESOURCE) unmatched.
  const valueMappingSignature = fieldsNeedingValueMapping.map((f) => `${f.uid}:${distinctValuesForField(f).join('|')}`).join('~');

  // Seed sensible defaults (exact case-insensitive option match, truthy guess for switches)
  // whenever the set of fields/values needing a mapping changes, without clobbering choices
  // the user already made.
  useEffect(() => {
    if (currentStepLabel !== 'Review Values') return;
    setValueMappings((prev) => {
      const next = { ...prev };
      fieldsNeedingValueMapping.forEach((field) => {
        const existing = { ...(next[field.uid] || {}) };
        const values = distinctValuesForField(field);
        values.forEach((raw) => {
          if (existing[raw] !== undefined) return;
          if (
            field.inputComponent === InputComponents.SELECT ||
            field.inputComponent === InputComponents.RADIO ||
            field.inputComponent === InputComponents.CHECKBOX
          ) {
            const match = (field.options || []).find((o: string) => normalizeHeader(o) === normalizeHeader(raw));
            existing[raw] = match || IGNORE_VALUE;
          } else if (field.inputComponent === InputComponents.SWITCH) {
            existing[raw] = isTruthy(raw);
          }
          // RESOURCE is left undefined until the user searches / auto-matches.
        });
        next[field.uid] = existing;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepLabel, valueMappingSignature]);

  /** Will this file value actually be submitted as something? `false` covers
   * both "not mapped yet" and "explicitly ignored" — either way nothing
   * reaches the form for it. A SWITCH mapped to `false` IS resolved. */
  const isValueResolved = (field: any, raw: string): boolean => {
    const mapped = valueMappings[field.uid]?.[raw];
    return mapped !== undefined && mapped !== null && mapped !== '' && mapped !== IGNORE_VALUE;
  };

  /** Row status marker. Green once a value will be submitted. Red only when
   * its absence is an actual problem — i.e. the field is required, so a row
   * carrying this value would stop at that step. Unresolved values on an
   * OPTIONAL field are neither: they're a deliberate, harmless omission, so
   * they get a neutral mark rather than an alarming one. */
  const renderValueStatus = (resolved: boolean, required: boolean, okTitle: string, badTitle: string, neutralTitle: string) => (
    <Tooltip title={resolved ? okTitle : required ? badTitle : neutralTitle}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {resolved ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : required ? (
          <CancelIcon color="error" fontSize="small" />
        ) : (
          <RemoveCircleOutlineIcon color="disabled" fontSize="small" />
        )}
      </Box>
    </Tooltip>
  );

  const setManualValue = (fieldUid: string, rowIndex: number, value: any) => {
    setManualValues((prev) => ({ ...prev, [fieldUid]: { ...(prev[fieldUid] || {}), [rowIndex]: value } }));
  };

  const setCellOverride = (fieldUid: string, rowIndex: number, value: any) => {
    setCellOverrides((prev) => ({ ...prev, [fieldUid]: { ...(prev[fieldUid] || {}), [rowIndex]: value } }));
  };

  /** Copy row 1's value down every remaining row — for the common case where
   * the value genuinely is the same throughout, without forcing it to be. */
  const fillDownManualValue = (fieldUid: string) => {
    setManualValues((prev) => {
      const first = prev[fieldUid]?.[0];
      if (first === undefined || first === null || first === '') return prev;
      const next: Record<number, any> = {};
      parsedRows.forEach((_, idx) => {
        next[idx] = first;
      });
      return { ...prev, [fieldUid]: next };
    });
  };

  const setValueMapping = (fieldUid: string, rawValue: string, mapped: any) => {
    setValueMappings((prev) => ({
      ...prev,
      [fieldUid]: { ...(prev[fieldUid] || {}), [rawValue]: mapped }
    }));
  };

  // The backend resource search matches a query against ONE field at a
  // time (data.firstName contains query OR data.lastName contains query OR
  // …) — so a full "Aarav Sharma" query never matches when a resource type
  // splits a name across separate fields, since neither field alone
  // contains that whole two-word string. A single word like "john" works
  // because it only ever needs to fit inside one field.
  //
  // To auto-match the multi-word case too, retry word by word when the
  // full-value search comes back empty, and only accept a candidate whose
  // OWN FIELD DATA — every field on the record, not just one — contains
  // EVERY word from the original value (resourceContainsAllWords above).
  // That guards against "Aarav" alone matching some unrelated resource
  // that just happens to share that first name, without assuming which
  // specific field the match landed in.
  //
  // Search across every resource type the field is actually configured to
  // search (resourceTypesForField) — NOT just field.resourceType alone.
  // Some fields (e.g. "Crew Information") are set up to search several
  // resource types at once, and a record can exist under one of the OTHER
  // types that field also searches — narrowing to a single type was
  // silently missing those, even when "the resource exists in the system".
  const findBestResourceMatch = async (resourceTypes: string[], raw: string): Promise<any | null> => {
    const runSearch = async (q: string) => {
      const resp: any = await searchResources(null, q, resourceTypes);
      // The backend returns { success: false, message: '...' } (not a thrown
      // error) for things like "Resource type not found" — that shape has no
      // array anywhere in it, so it was silently turning into the exact same
      // empty result as "searched fine, nothing matched". Logging the failure
      // explicitly makes that distinction visible in the console instead of
      // just looking like the record doesn't exist.
      if (resp && resp.success === false) {
        // eslint-disable-next-line no-console
        console.warn('[ImportTransactionsDialog] resource search failed:', { query: q, resourceTypes, response: resp });
      }
      // The actual shape coming back is { data: { data: [...] } } — this was
      // only checking resp.data and resp themselves for an array, missing the
      // real nested resp.data.data array entirely, so every search silently
      // returned nothing no matter what the API actually found (confirmed via
      // network capture: query=IAD2 came back with a direct "Site Name": "IAD2"
      // match sitting right there in data.data[0]). Matches the same
      // extraction ResourceValueRow's own manual-search effect already uses.
      return Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.data?.data) ? resp.data.data : [];
    };

    const direct = await runSearch(raw);
    if (direct[0]) return direct[0];

    const words = raw.split(/\s+/).filter((w) => w.length > 1);
    if (words.length <= 1) return null;

    const candidatesById = new Map<string, any>();
    for (const word of words) {
      const found = await runSearch(word);
      found.forEach((c: any) => {
        const id = c.id || c._id;
        if (id && !candidatesById.has(id)) candidatesById.set(id, c);
      });
    }

    const normalizedWords = words.map((w) => w.toLowerCase());
    for (const candidate of candidatesById.values()) {
      if (resourceContainsAllWords(candidate, normalizedWords)) return candidate;
    }
    return null;
  };

  const handleAutoMatchResource = async (field: any) => {
    // Resolved one value at a time (a sequential for-of + await), this could
    // take several seconds to reach the LAST row of a field with more than a
    // couple of distinct values — every value's search, including the
    // multi-word fallback searches inside findBestResourceMatch, waited on
    // the one before it even though they're entirely independent lookups.
    // Running them concurrently gets every row to its resolved state in
    // roughly the time of the single slowest lookup instead of the sum of
    // all of them — much closer to how instantly the SELECT rows resolve.
    const values = distinctValuesForField(field).filter((raw) => !valueMappings[field.uid]?.[raw]);
    if (values.length === 0) return;
    const resourceTypes = resourceTypesForField(field);
    // No point firing a request the backend is guaranteed to 404 — see the
    // console.warn inside resourceTypesForField for why this happens and
    // what it means (the field's workflow config has no resource type on
    // it). Manual search from this row will fail the same way until that's
    // fixed upstream, which the "No resource type configured" note in the
    // UI now says explicitly instead of leaving the row looking stuck.
    if (resourceTypes.length === 0) return;
    setResourceMatching((prev) => ({ ...prev, [field.uid]: true }));
    await Promise.all(
      values.map(async (raw) => {
        try {
          const match = await findBestResourceMatch(resourceTypes, raw);
          if (match) {
            setValueMapping(field.uid, raw, {
              id: match.id,
              fields: match.fields,
              category: match.category || match.resourceType?.category || match.resourceTypeId?.category
            });
          }
        } catch {
          // leave unresolved — user can search manually
        }
      })
    );
    setResourceMatching((prev) => ({ ...prev, [field.uid]: false }));
  };

  // Auto-match RESOURCE fields whenever the Mapping step is (re)entered or a
  // column mapping changes — there's no button for this, so this is the only
  // thing that ever runs it. Safe to re-run repeatedly because
  // handleAutoMatchResource itself skips any value that already has a mapping
  // — so a re-run only retries whatever is STILL unresolved (e.g. a search
  // that raced with something else the first time, or a resource that's since
  // been added) without repeating work or clobbering rows already matched or
  // manually picked.
  useEffect(() => {
    if (currentStepLabel !== 'Review Values') return;
    fieldsNeedingValueMapping
      .filter((field) => field.inputComponent === InputComponents.RESOURCE)
      .forEach((field) => {
        handleAutoMatchResource(field);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepLabel, valueMappingSignature]);

  // ---------- Value resolution / payload building ----------

  /** Put a hand-entered value into the same shape getMappedValue produces for
   * a mapped column, so the payload builder can't tell the two apart. */
  const coerceManualValue = (field: any, v: any): any => {
    switch (field.inputComponent) {
      case InputComponents.NUMBER: {
        if (v === undefined || v === null || v === '') return '';
        const n = Number(v);
        return Number.isNaN(n) ? '' : n;
      }
      case InputComponents.DATE:
        if (v === undefined || v === null || v === '') return null;
        return createDateWithTimezone(v, getCurrentTimezone(), !!field.settings?.isTimeRequired, !!field.settings?.includeTimezone);
      case InputComponents.CHECKBOX:
        return Array.isArray(v) ? v : v ? [v] : [];
      case InputComponents.SWITCH:
        return v === undefined || v === null ? null : !!v;
      case InputComponents.RESOURCE:
        return v ?? null;
      case InputComponents.TEXT:
        return v === undefined || v === null || v === '' ? [] : [String(v)];
      default:
        return v ?? '';
    }
  };

  const getMappedValue = (field: any, row: Record<string, any>, rowIndex: number): any => {
    const col = fieldMapping[field.uid];
    const rawCell = col ? row[col] : undefined;
    // An inline correction the user typed in Review Values wins over the file's
    // raw cell, for NUMBER/DATE only (the only types that offer one).
    const override = cellOverrides[field.uid]?.[rowIndex];
    const raw = override !== undefined ? override : rawCell;
    const vm = valueMappings[field.uid] || {};

    // No column feeds this field, so the only possible source is whatever the
    // user typed for THIS row on the Value Mapping screen.
    if (!col) return coerceManualValue(field, manualValues[field.uid]?.[rowIndex]);

    switch (field.inputComponent) {
      case InputComponents.NUMBER: {
        if (raw === undefined || raw === '') return '';
        const n = Number(raw);
        return Number.isNaN(n) ? '' : n;
      }
      case InputComponents.DATE:
        // Single source of truth for raw -> date object, shared with the
        // Review Values format check (cellFormatError) so the two can't drift.
        return resolveDateValue(field, raw);
      case InputComponents.CHECKBOX:
        return splitMulti(raw)
          .map((t) => vm[t])
          .filter((v) => v !== undefined && v !== null && v !== IGNORE_VALUE);
      case InputComponents.SELECT:
      case InputComponents.RADIO: {
        const key = String(raw ?? '').trim();
        if (!key) return '';
        const mapped = vm[key];
        return mapped === IGNORE_VALUE || mapped === undefined ? '' : mapped;
      }
      case InputComponents.SWITCH: {
        const key = String(raw ?? '').trim();
        const mapped = vm[key];
        return mapped === undefined ? isTruthy(raw) : !!mapped;
      }
      case InputComponents.RESOURCE: {
        const key = String(raw ?? '').trim();
        return key ? vm[key] ?? null : null;
      }
      case InputComponents.TEXT:
        return raw === undefined || raw === '' ? [] : [String(raw)];
      default:
        return raw ?? '';
    }
  };

  const isValueEmpty = (field: any, value: any): boolean => {
    if (field.inputComponent === InputComponents.CHECKBOX) return !Array.isArray(value) || value.length === 0;
    if (field.inputComponent === InputComponents.SWITCH) return value === undefined || value === null;
    if (field.inputComponent === InputComponents.RESOURCE) return !value;
    if (field.inputComponent === InputComponents.TEXT) return !Array.isArray(value) || value.length === 0;
    return value === undefined || value === null || value === '';
  };

  const buildEventDataForFields = (fields: any[], row: Record<string, any>, rowIndex: number) => {
    return fields.map((field) => {
      const base = {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        inputComponent: field.inputComponent,
        children: [] as any[]
      };
      const value = getMappedValue(field, row, rowIndex);
      if (field.inputComponent === InputComponents.SWITCH) {
        return { ...base, value: { value: [value], files: [] } };
      }
      return { ...base, value: { value, files: [] } };
    });
  };

  // ---------- Import ----------

  /** Drive one row through every reachable step, in the precomputed
   * full-process order (see reachableSteps above) — NOT the server's own
   * "what step comes next" hint, which is role-filtered for whoever's
   * submitting and unreliable once import is deliberately crossing role
   * boundaries. If a step submission itself fails or required data is
   * missing, that's still caught and reported per row. */
  const importOneRow = async (row: Record<string, any>, currentLocation: any, rowIndex: number): Promise<RowResult> => {
    let transactionUid: string | undefined;
    let stepsCompleted = 0;
    let stepsSkipped = 0;
    let stopReason: string | null = null;

    for (let idx = 0; idx < reachableSteps.length; idx++) {
      const step = reachableSteps[idx];
      const fields = step.component?.fields || [];

      // No column was mapped to any field on this step, so it isn't part of
      // this import — skip it outright, the same as an all-empty step,
      // without even checking individual field values.
      if (!includedStepUids.has(step.uid)) {
        stepsSkipped++;
        continue;
      }

      // If this row simply has no data at all for this step — every field
      // comes back empty, whether because the spreadsheet cells were left
      // blank or because every field on the step is a type that can never
      // come from a spreadsheet (photo/signature/file/sub-form) — there's
      // nothing to submit, so skip the step entirely for this row rather
      // than sending an all-blank payload. This is independent of whether
      // any of those empty fields are marked required: an empty step just
      // isn't part of this row's data. Whatever transactionUid has already
      // been captured from an earlier step carries forward untouched, so
      // the next step that DOES have data still chains onto the same
      // transaction.
      const hasAnyData = fields.some((f: any) => !isValueEmpty(f, getMappedValue(f, row, rowIndex)));
      if (!hasAnyData) {
        stepsSkipped++;
        continue;
      }

      // A required photo/signature/file/sub-form field is never treated as
      // "missing" here — there's no way to supply one from a spreadsheet, so
      // it's excluded from validation entirely and submitted blank instead
      // of blocking the row. RESOURCE fields are excluded too: matching one
      // depends on a live search actually finding a record, which can't be
      // guaranteed for every row in a batch, so an unresolved required
      // resource is submitted as empty rather than stopping the row.
      const missing = fields
        .filter(
          (f: any) =>
            !NON_IMPORTABLE_TYPES.includes(f.inputComponent) &&
            f.inputComponent !== InputComponents.RESOURCE &&
            f.settings?.required &&
            isValueEmpty(f, getMappedValue(f, row, rowIndex))
        )
        .map((f: any) => f.question);
      if (missing.length > 0) {
        stopReason = `stopped before "${stepLabel(step)}" — missing required value(s): ${missing.join(', ')}`;
        break;
      }

      const data = buildEventDataForFields(fields, row, rowIndex);
      const payload: any = {
        comment: '',
        eventSource: 'WEB',
        eventType: step.stepType,
        step: step.uid,
        browser: { ...(browserDetails || {}), fingerprint, publicIp },
        location: currentLocation,
        data,
        ...(transactionUid ? { key: transactionUid } : {}),
        teamUid: selectedTeam
      };

      let resp: any;
      try {
        resp = await uploadEventData(payload);
      } catch (err: any) {
        stopReason = `failed at "${stepLabel(step)}": ${err?.message || 'unexpected error'}`;
        break;
      }

      if (!resp?.success) {
        stopReason = `failed at "${stepLabel(step)}": ${resp?.message || resp?.data || 'request failed'}`;
        break;
      }
      stepsCompleted++;

      const nextInfo = resp.data?.nextStep;
      if (nextInfo?.uid) transactionUid = nextInfo.uid;

      // The server's "what's next" hint (nextInfo.step) is computed for the
      // SUBMITTING user's own role — it only ever returns a step whose actor
      // matches them, searching ahead through the remaining steps for one
      // that does. That makes it unusable as a branch signal for bulk import:
      // it comes back null when no later step matches the importing user's
      // role at all, and when it IS non-null it can point past the very next
      // step in the process (skipping ahead to whatever step that role CAN
      // act on) rather than the step the process actually goes to next. So
      // import intentionally ignores it here and always drives every row
      // through its own precomputed, full-process step order below — this is
      // what makes "import every step regardless of role" possible at all.
      if (!reachableSteps[idx + 1]) break; // finished every step we had data for — done
    }

    const skippedNote = stepsSkipped > 0 ? ` (${stepsSkipped} step${stepsSkipped === 1 ? '' : 's'} skipped — no data for this row)` : '';

    // Counted against the steps this import actually covers, not every step
    // in the process — "completed 2/10" would read as a failure when 8 of
    // those steps were never part of the import in the first place.
    const targetStepCount = includedStepUids.size;

    if (stepsCompleted === 0) {
      return {
        row: 0,
        status: 'error',
        message: stopReason || `No data found for this row across any of the ${targetStepCount} mapped step(s) — nothing to import`
      };
    }
    if (stopReason) {
      return {
        row: 0,
        status: 'partial',
        message: `Created — completed ${stepsCompleted}/${targetStepCount} form step(s), ${stopReason}${skippedNote}`
      };
    }
    return { row: 0, status: 'success', message: `Created — all ${stepsCompleted} form step(s) completed${skippedNote}` };
  };

  const runImport = async () => {
    if (reachableSteps.length === 0) return;
    const rows = parsedRows;
    setExpectedCount(rows.length);
    setImporting(true);
    setResults([]);

    let currentLocation = location;
    try {
      currentLocation = (await captureLocationWithAddress()) || currentLocation;
    } catch {
      // fall back to whatever location context already had
    }

    const collected: RowResult[] = [];
    for (let i = 0; i < rows.length; i++) {
      const result = await importOneRow(rows[i], currentLocation, i);
      collected.push({ ...result, row: i + 1 });
      setResults([...collected]);
    }

    setImporting(false);
    const successCount = collected.filter((r) => r.status !== 'error').length;
    if (successCount > 0) {
      showToast(
        `Imported ${successCount} of ${rows.length} transaction${rows.length === 1 ? '' : 's'}`,
        successCount === rows.length ? 'success' : 'warning'
      );
      onImported?.();
    } else {
      showToast('No transactions were created. See the details below.', 'error');
    }
  };

  /**
   * Remember this process's mapping so the next import of a similar file
   * comes back pre-filled. Fire-and-forget by design: a persistence hiccup
   * only costs the user that convenience next time, and must never block or
   * fail an import that is already underway.
   */
  const persistMapping = () => {
    if (!selectedTeam || selectedTeam === 'all') return;
    try {
      saveImportMapping(selectedTeam, toWireMapping(columns, fieldMapping, importableFields)).catch((err: any) => {
        // eslint-disable-next-line no-console
        console.warn('[ImportTransactionsDialog] could not save the mapping for this process:', err);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ImportTransactionsDialog] could not save the mapping for this process:', err);
    }
  };

  /** Which rows are still blank for a hand-entered required field. */
  const missingRowsForField = (field: any): number[] =>
    parsedRows.reduce<number[]>((acc, _row, idx) => {
      if (isValueEmpty(field, coerceManualValue(field, manualValues[field.uid]?.[idx]))) acc.push(idx);
      return acc;
    }, []);

  // Required fields with no column that still have at least one blank row.
  // These genuinely can't be left blank: the step's form submission needs
  // them, so those rows would otherwise stop at that step with "missing
  // required value". Hence they hold back Value Mapping (and Import) — but not
  // Field Mapping, where a required field simply missing from the file is
  // normal.
  const requiredValuesStillMissing = requiredFieldsNeedingValue.filter((f) => missingRowsForField(f).length > 0);

  // A required field with no column no longer blocks the mapping step — it's
  // collected on Value Mapping instead (see requiredFieldsNeedingValue), so
  // the mapping step only insists on a parsed file with SOMETHING mapped.
  const canGoNext = (() => {
    if (currentStepLabel === 'Upload') return parsedRows.length > 0 && !parseError && !isBlocked;
    if (currentStepLabel === 'Field Mapping') return includedStepUids.size > 0;
    if (currentStepLabel === 'Review Values') return requiredValuesStillMissing.length === 0 && !hasUnresolvedFormatErrors;
    return true;
  })();

  const handleNext = () => {
    if (currentStepLabel === 'Field Mapping') persistMapping();
    setActiveStepIndex((i) => Math.min(i + 1, wizardSteps.length - 1));
  };
  const handleBack = () => setActiveStepIndex((i) => Math.max(i - 1, 0));

  const successCount = results.filter((r) => r.status === 'success').length;
  const partialCount = results.filter((r) => r.status === 'partial').length;
  const failureCount = results.filter((r) => r.status === 'error').length;

  const canImport =
    !isBlocked &&
    !loadingFields &&
    !loadError &&
    parsedRows.length > 0 &&
    !importing &&
    requiredValuesStillMissing.length === 0 &&
    !hasUnresolvedFormatErrors;

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Import Transactions
        <IconButton size="small" onClick={onClose} disabled={importing}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create transactions for <strong>{processName}</strong> from a file instead of filling the form by hand. Every step in this
          process's workflow — not just the ones your own role can normally act on — can be filled in from the same file.
        </Typography>

        {loadingFields && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading the fields for this process…</Typography>
          </Box>
        )}

        {!loadingFields && loadError && <Alert severity="error">{loadError}</Alert>}

        {!loadingFields && !loadError && isBlocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            None of the steps in this process have any fillable fields, so it can't be created from a document. Please use{' '}
            <strong>Add Transaction</strong> for this process instead.
          </Alert>
        )}

        {!loadingFields && !loadError && !isBlocked && (
          <>
            <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ mb: 3 }}>
              {wizardSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* ---- Upload ---- */}
            {currentStepLabel === 'Upload' && (
              <Box>
                {/* Its own section, not a wizard step — see the component's
                    own note for why choosing steps here doesn't gate upload. */}
                <DownloadTemplateSection
                  processName={processName}
                  reachableSteps={reachableSteps}
                  importableFields={importableFields}
                  displayLabel={displayLabel}
                  showToast={showToast}
                />

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Upload an Excel or CSV file with a header row and one row per transaction you want to create — as many rows as you need.
                  Which workflow steps get filled in is decided on the next screen, simply by which of your columns you map to which field.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                  <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={handleChooseFile}>
                    {fileName ? 'Choose a different file' : 'Upload File'}
                  </Button>
                  <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFileChange} />
                  {fileName && <Chip size="small" icon={<InsertDriveFileIcon />} label={fileName} />}
                </Box>

                {parseError && <Alert severity="error">{parseError}</Alert>}

                {parsedRows.length > 0 && !parseError && (
                  <Alert severity="success">
                    {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'} detected, {columns.length} column
                    {columns.length === 1 ? '' : 's'} found.
                  </Alert>
                )}

                {parsedRows.length > 0 && !parseError && appliedSavedMapping && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Reused the field mapping saved for this process — review it on the next screen.
                  </Alert>
                )}

                {parsedRows.length > 0 && !parseError && missingSavedColumns.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This file doesn't have {missingSavedColumns.length === 1 ? 'a column' : 'columns'} the saved mapping expected:{' '}
                    <strong>{missingSavedColumns.join(', ')}</strong>. Those field{missingSavedColumns.length === 1 ? '' : 's'} are left
                    unmapped — map them again on the next screen if you need them.
                  </Alert>
                )}
              </Box>
            )}

            {/* ---- Mapping: columns -> fields, then the values inside those
                 columns, on one screen ---- */}
            {currentStepLabel === 'Field Mapping' && (
              <Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Button size="small" startIcon={<AutoFixHighIcon />} onClick={handleAutoMapByName}>
                      Auto-map by name
                    </Button>
                    <Button size="small" onClick={handleClearMapping}>
                      Clear
                    </Button>
                  </Box>

                  {requiredFieldsNeedingValue.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No column fills these required fields:{' '}
                      <strong>{requiredFieldsNeedingValue.map((f) => displayLabel(f)).join(', ')}</strong>. You can carry on — the next
                      screen asks for a value for each row.
                    </Alert>
                  )}

                  {includedStepUids.size === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Point at least one column at a workflow field below to continue — nothing is imported until you do.
                    </Alert>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 48 }} />
                          <TableCell>Column in file</TableCell>
                          <TableCell sx={{ width: '55%' }}>Workflow field</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {columns.map((column) => {
                          const mappedUid = columnToFieldUid[column] || '';
                          const mappedField = mappedUid ? importableFields.find((f) => f.uid === mappedUid) : null;
                          return (
                            <TableRow key={column}>
                              {/* At-a-glance status for the row: is this column
                                actually going anywhere, or will it be ignored? */}
                              <TableCell sx={{ width: 48 }}>
                                <Tooltip
                                  title={
                                    mappedField
                                      ? `Matched to “${displayLabel(mappedField)}”`
                                      : 'Not matched — this column will not be imported'
                                  }
                                >
                                  {/* Tooltip needs one ref-holding child, so the
                                    icon is wrapped rather than swapped bare. */}
                                  <Box component="span" sx={{ display: 'inline-flex' }}>
                                    {mappedField ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                      <CancelIcon color="error" fontSize="small" />
                                    )}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>{column}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FormControl size="small" fullWidth>
                                    <Select
                                      displayEmpty
                                      value={mappedUid}
                                      onChange={(e) => setColumnTarget(column, e.target.value as string)}
                                    >
                                      <MenuItem value="">
                                        <em>Do not import</em>
                                      </MenuItem>
                                      {/* A field already fed by another column stays selectable —
                                        picking it moves the field over rather than creating a
                                        second source for it, and that column's row drops back
                                        to "Do not import". */}
                                      {fieldOptionsByStep.map(({ step, fields }) => [
                                        <ListSubheader key={`${step.uid}-header`}>{stepLabel(step)}</ListSubheader>,
                                        ...fields.map((field) => (
                                          <MenuItem key={field.uid} value={field.uid}>
                                            {displayLabel(field)}
                                            {field.settings?.required && (
                                              <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                                                *
                                              </Typography>
                                            )}
                                          </MenuItem>
                                        ))
                                      ])}
                                    </Select>
                                  </FormControl>
                                  {mappedField && <Chip size="small" label={mappedField.inputComponent} />}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}

            {/* ---- Review Values ---- */}
            {currentStepLabel === 'Review Values' && (
              <Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your file&apos;s columns are matched to the right fields. Now check the values inside them: where a value in your file
                    is written differently from what this process expects, choose what it means, and type in anything your file doesn&apos;t
                    have a column for.
                  </Typography>

                  {requiredValuesStillMissing.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Still needed: <strong>{requiredValuesStillMissing.map((f) => displayLabel(f)).join(', ')}</strong>. The step&apos;s
                      form can&apos;t be submitted without these, so every row needs a value to continue.
                    </Alert>
                  )}

                  {/* Mapped NUMBER/DATE columns whose file values are the wrong
                    type — text in a number column, an unparseable date. The
                    user fixes each one right here (cellOverrides). Corrected
                    rows stay visible as resolved rather than vanishing, so the
                    input doesn't unmount mid-edit. Next / Import stay disabled
                    until every flagged value is fixed (hasUnresolvedFormatErrors). */}
                  {showFormatSection && (
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Some values don&apos;t match the type of the field they&apos;re mapped to. Correct them right here — each row turns
                        green once its value is valid. You can&apos;t continue until every flagged value is fixed.
                      </Alert>
                      {fieldsWithFormatRows.map(({ field, rows }) => {
                        const remaining = rows.filter((r) => !r.valid).length;
                        return (
                          <Box key={`fmt-${field.uid}`} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                              {displayLabel(field)}{' '}
                              <Chip size="small" color={remaining ? 'warning' : 'success'} label={field.inputComponent} sx={{ ml: 1 }} />
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              Column <strong>{fieldMapping[field.uid]}</strong> on <strong>{stepLabel(field.__step)}</strong> —{' '}
                              {remaining > 0
                                ? `${remaining} value${remaining === 1 ? '' : 's'} ${
                                    field.inputComponent === InputComponents.NUMBER ? "aren't numbers" : "aren't valid dates"
                                  } yet.`
                                : 'all corrected.'}{' '}
                              Expected format: <strong>{expectedFormatHint(field)}</strong>.
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflowY: 'auto' }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: 48 }} />
                                    <TableCell sx={{ width: 90, whiteSpace: 'nowrap' }}>Row In File</TableCell>
                                    <TableCell>Value in file</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Corrected value</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rows.map((e) => (
                                    <TableRow key={`${field.uid}-${e.rowIndex}`}>
                                      <TableCell sx={{ width: 48 }}>
                                        <Tooltip
                                          title={e.valid ? 'Corrected — this value will be imported' : 'Wrong type — correct it here to continue'}
                                        >
                                          <Box component="span" sx={{ display: 'inline-flex' }}>
                                            {e.valid ? (
                                              <CheckCircleIcon color="success" fontSize="small" />
                                            ) : (
                                              <WarningAmberIcon color="warning" fontSize="small" />
                                            )}
                                          </Box>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>{e.row}</TableCell>
                                      <TableCell>{e.value}</TableCell>
                                      <TableCell sx={{ minWidth: 200 }}>
                                        {/* The same input the form itself uses for
                                          this field type — a real date / datetime
                                          picker for DATE, a number field for
                                          NUMBER — not a free-text box. */}
                                        <ManualValueInput
                                          field={field}
                                          value={cellOverrides[field.uid]?.[e.rowIndex] ?? ''}
                                          onChange={(v) => setCellOverride(field.uid, e.rowIndex, v)}
                                          searchResources={searchResources}
                                          getResourceTypeById={getResourceTypeById}
                                          createResourceData={createResourceData}
                                          showToast={showToast}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Required fields no column fills — typed in per row, since
                    these are usually different for every transaction. */}
                  {requiredFieldsNeedingValue.map((field) => {
                    const missingRows = missingRowsForField(field);
                    const firstValue = manualValues[field.uid]?.[0];
                    return (
                      <Box key={`manual-${field.uid}`} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {displayLabel(field)}
                            <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                            <Chip size="small" label={field.inputComponent} sx={{ ml: 1 }} />
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => fillDownManualValue(field.uid)}
                            disabled={firstValue === undefined || firstValue === null || firstValue === ''}
                          >
                            Copy row 1 to all rows
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Your file has no column for this required field on <strong>{stepLabel(field.__step)}</strong>, so enter a value
                          for each row.
                          {missingRows.length > 0 && ` ${missingRows.length} of ${parsedRows.length} still blank.`}
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflowY: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: 48 }} />
                                <TableCell sx={{ width: 90, whiteSpace: 'nowrap' }}>Row In File</TableCell>
                                <TableCell>Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {parsedRows.map((_row, idx) => {
                                // Always "required" here — this whole table only
                                // exists for required fields — so a blank row is
                                // unambiguously red.
                                const filled = !isValueEmpty(field, coerceManualValue(field, manualValues[field.uid]?.[idx]));
                                return (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ width: 48 }}>
                                      {renderValueStatus(filled, true, 'Value entered', 'Required — this row has no value yet', '')}
                                    </TableCell>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>
                                      <ManualValueInput
                                        field={field}
                                        value={manualValues[field.uid]?.[idx]}
                                        onChange={(v) => setManualValue(field.uid, idx, v)}
                                        searchResources={searchResources}
                                        getResourceTypeById={getResourceTypeById}
                                        createResourceData={createResourceData}
                                        showToast={showToast}
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}

                  {fieldsNeedingValueMapping.map((field) => {
                    const values = distinctValuesForField(field);
                    const isResource = field.inputComponent === InputComponents.RESOURCE;
                    const resourceTypes = isResource ? resourceTypesForField(field) : [];
                    return (
                      <Box key={field.uid} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {displayLabel(field)} <Chip size="small" label={field.inputComponent} sx={{ ml: 1 }} />
                          </Typography>
                          {isResource && resourceMatching[field.uid] && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={14} />
                              <Typography variant="caption" color="text.secondary">
                                Matching…
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {isResource && resourceTypes.length === 0 && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            This field is not configured with a resource type to search against in this workflow, so it cannot auto-match or
                            be searched manually. This needs to be fixed in the workflow field settings, not in this import.
                          </Alert>
                        )}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflowY: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: 48 }} />
                                <TableCell sx={{ width: 90, whiteSpace: 'nowrap' }}>Row In File</TableCell>
                                <TableCell>Value in file</TableCell>
                                <TableCell>Maps to</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {values.map((raw) => (
                                <TableRow key={raw}>
                                  <TableCell sx={{ width: 48 }}>
                                    {renderValueStatus(
                                      isValueResolved(field, raw),
                                      !!field.settings?.required,
                                      'Matched — this value will be submitted',
                                      'Not matched, and this field is required — rows with this value will fail',
                                      'Not matched — this value will be left out'
                                    )}
                                  </TableCell>
                                  <TableCell>{rowNumbersForValue(field, raw).join(', ')}</TableCell>
                                  <TableCell>{raw}</TableCell>
                                  <TableCell>
                                    {isResource ? (
                                      <ResourceValueRow
                                        rawValue={raw}
                                        resourceTypes={resourceTypes}
                                        value={valueMappings[field.uid]?.[raw]}
                                        onChange={(v) => setValueMapping(field.uid, raw, v)}
                                        searchResources={searchResources}
                                        getResourceTypeById={getResourceTypeById}
                                        createResourceData={createResourceData}
                                        showToast={showToast}
                                      />
                                    ) : field.inputComponent === InputComponents.SWITCH ? (
                                      <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <Select
                                          value={
                                            valueMappings[field.uid]?.[raw] === undefined ? '' : String(!!valueMappings[field.uid]?.[raw])
                                          }
                                          onChange={(e) => setValueMapping(field.uid, raw, e.target.value === 'true')}
                                        >
                                          <MenuItem value="true">True</MenuItem>
                                          <MenuItem value="false">False</MenuItem>
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <FormControl size="small" sx={{ minWidth: 180 }}>
                                        <Select
                                          displayEmpty
                                          value={valueMappings[field.uid]?.[raw] ?? ''}
                                          onChange={(e) => setValueMapping(field.uid, raw, e.target.value)}
                                        >
                                          <MenuItem value={IGNORE_VALUE}>
                                            <em>Ignore this value</em>
                                          </MenuItem>
                                          {(field.options || []).map((opt: string) => (
                                            <MenuItem key={opt} value={opt}>
                                              {opt}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ---- Import ---- */}
            {currentStepLabel === 'Import' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Ready to import {parsedRows.length} transaction{parsedRows.length === 1 ? '' : 's'} into <strong>{processName}</strong>,
                  driving each through the {includedStepUids.size} mapped step{includedStepUids.size === 1 ? '' : 's'}.
                </Typography>

                {importing && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={expectedCount ? (results.length / expectedCount) * 100 : 0} />
                    <Typography variant="caption" color="text.secondary">
                      Processing {results.length} of {expectedCount}…
                    </Typography>
                  </Box>
                )}

                {results.length > 0 && (
                  <>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {successCount} fully completed, {partialCount} partially completed, {failureCount} failed
                    </Typography>
                    <List dense sx={{ maxHeight: 260, overflowY: 'auto' }}>
                      {results.map((r) => (
                        <ListItem key={r.row} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {r.status === 'success' ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : r.status === 'partial' ? (
                              <WarningAmberIcon color="warning" fontSize="small" />
                            ) : (
                              <ErrorIcon color="error" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText primary={`Row ${r.row}`} secondary={r.message} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={importing}>
          {results.length > 0 ? 'Close' : 'Cancel'}
        </Button>
        {!loadingFields && !loadError && !isBlocked && currentStepLabel !== 'Upload' && (
          <Button onClick={handleBack} disabled={importing}>
            Back
          </Button>
        )}
        {!loadingFields && !loadError && !isBlocked && currentStepLabel !== 'Import' && (
          <Button variant="contained" onClick={handleNext} disabled={!canGoNext}>
            Next
          </Button>
        )}
        {currentStepLabel === 'Import' && (
          <Button variant="contained" onClick={runImport} disabled={!canImport || results.length > 0}>
            {importing ? <CircularProgress size={20} color="inherit" /> : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportTransactionsDialog;
